/* ---------------------------------------------------------------------------
   Bangladesh, Roads and Highways Department — the fourth department, and the
   second to start with no register in it.

   ── THERE IS NO BUNDLED REGISTER ──

   The structures are to be entered in the application, so nothing is seeded:
   `seedFor('bd-rhd')` returns [] and every screen says the register is empty
   until the first record lands in D1. Same arrangement as `pk-fwo` before its
   demonstration — see the header of src/tenant/profiles/pk-fwo.ts.

   ── WHAT IS SOURCED AND WHAT IS PROVISIONAL ──

   SOURCED. The department, the ministry and its division are as the Government
   of Bangladesh names them: the Roads and Highways Department sits under the
   Road Transport and Highways Division of the Ministry of Road Transport and
   Bridges. The outline and bounds are Natural Earth's Bangladesh point-of-view
   edition — see src/data/bangladesh.ts.

   PROVISIONAL. `unit`, `unitShort`, the vocabulary, `chainage` and the road
   classes are this project's reading of RHD practice, not RHD's own documents.
   They print on an inventory sheet somebody files: confirm them against one RHD
   drawing and one RHD inventory form, and treat a correction as a one-line
   change.

   NOTHING HERE IS ENGINEERING. Same rule as every profile: the element list,
   the condition scales, the screening and the quantity methods are the same for
   every department.
   --------------------------------------------------------------------------- */

import { BANGLADESH_BOUNDS, BANGLADESH_OUTLINE } from '../../data/bangladesh.ts';
import { classifyRoad } from '../roads.ts';
import type { RoadClassRule, TenantProfile } from '../types.ts';

/**
 * RHD's road classification, from the code a route carries: `N` national
 * highways, `R` regional highways, `Z` zila (district) roads.
 *
 * UNVERIFIED AGAINST ANY RHD RECORD — the register is empty. An unmatched code
 * groups as *not recorded* rather than being bucketed, so a code nobody
 * anticipated is visible rather than wrong. Check against the first records.
 */
const ROAD_CLASSES: readonly RoadClassRule[] = [
  { match: /^N-?\d/, label: 'National highway' },
  { match: /^R-?\d/, label: 'Regional highway' },
  { match: /^Z-?\d/, label: 'Zila road' },
];

export const BD_RHD: TenantProfile = {
  id: 'bd-rhd',
  name: 'Bangladesh — Roads and Highways Department',
  country: 'BD',
  countryName: 'Bangladesh',

  agency: {
    government: 'Government of the People’s Republic of Bangladesh',
    ministry: 'Ministry of Road Transport and Bridges',
    department: 'Roads and Highways Department',
    departmentShort: 'RHD',
    branch: 'Road Transport and Highways Division',
    /* PROVISIONAL — the office the register is kept for has not been named. */
    unit: 'Bridge Management Wing',
    unitShort: 'Bridge Management Wing',
    seal: 'BD',
  },

  brand: {
    emblems: [],
    logo: null,
    /* The flag's green and red. */
    ground: '#006a4e',
    stripe: ['#f42a41', '#006a4e'],
  },

  /* National bounds and no `home`, as for `pk-fwo`: until records carry
     coordinates there is no corridor to open on, and `fitHome` in
     src/map/nepalMap.ts fits the records once they do. */
  geo: {
    bounds: BANGLADESH_BOUNDS,
    outline: BANGLADESH_OUTLINE,
    outlineProvenance:
      'Natural Earth 1:10m Admin 0 Countries, Bangladesh point-of-view edition ' +
      '(ne_10m_admin_0_countries_bdg), public domain, largest ring simplified to 494 points ' +
      'by Douglas–Peucker at a 0.0126° tolerance; the offshore islands are not drawn. ' +
      'A cartographic outline at country scale, not a survey boundary.',
  },

  /* PROVISIONAL. Division, then district (zila), then upazila and union;
     `locality` takes the union as the closest analogue to DoR's VDC and DPWH's
     barangay. RHD's own field units are zones, circles and road divisions —
     `office` is the road division, which is the one a structure is maintained
     from. */
  vocab: {
    area: 'District',
    areaPlural: 'Districts',
    region: 'Division',
    office: 'Road division',
    locality: 'Union',
    road: 'Road',
    chainage: 'Chainage',
  },

  /* PROVISIONAL. Kilometre chainage, as Nepal writes it. */
  chainage: 'km',

  roadClasses: ROAD_CLASSES,

  /* None until an RHD inventory form has been seen — see the note on `extras`
     in src/tenant/types.ts. */
  extras: [],

  groupings: [
    { key: 'class', label: 'Road classification', of: (b) => classifyRoad(b.route, ROAD_CLASSES) },
    { key: 'district', label: 'District', of: (b) => b.district || null },
    { key: 'region', label: 'Division', of: (b) => b.province || null },
    { key: 'office', label: 'Road division', of: (b) => b.divisionOffice || null },
  ],

  currency: {
    code: 'BDT',
    symbol: 'Tk',
    unit: 'taka',
    /* Bangladesh's fiscal year runs July to June and is written as a pair. */
    fiscalPeriodLabel: 'Fiscal year, July to June (e.g. 2026-27)',
  },

  /* English only. Bangla is not half-translated on the strength of a guess —
     see the header of src/data/locale.ts. */
  locales: ['en'],

  /* NULL until somebody cites BNBC 2020's seismic zoning in words it actually
     uses — see `seismicZoningNote` in src/tenant/types.ts and the note on it in
     src/tenant/profiles/pk-fwo.ts. */
  seismicZoningNote: null,

  registerProvenanceShort: 'No structures yet — entered by RHD',

  registerProvenance:
    'Empty. This department has been configured so the application can be used as ' +
    'Bangladesh, and its structures are entered here rather than bundled. Nothing is ' +
    'seeded: every record on screen is one somebody entered for RHD.',

  /* PROVISIONAL (LC-24), as for every department. */
  rerating: { years: 5, restrictedYears: 2 },

  /* PROVISIONAL (LC-61), as for every department. */
  inspection: { routineMonths: 12, principalMonths: 60, shortRoutineMonths: 6, shortPrincipalMonths: 36, poorFrom: 0.75 },
};
